import { useCallback, useEffect, useState } from "react";
import { ArrowUp as ArrowUpIcon, ChevronRight as ChevronRightIcon } from "@tamagui/lucide-icons";
import { Body3, Subtitle2, XStack, YStack } from "@medbillai/ui";
import { createParam } from "@medbillai/navigation/params";
import { analyticsService } from "../../../lib/analytics";
import { useCoveragePlanStore } from "../coveragePlanStore";
import { money, shortDate } from "../format";
import { PressableStack } from "../PressableStack";
import type { CoveragePlan, CoverageRead, Movement } from "../read/readCoverage";
import { MovementSheet, movementFigure } from "./MovementSheet";
import { useMovementNotice } from "./useMovementNotice";
const {
  useParams
} = createParam<{
  movement?: string;
  membershipId?: string;
}>();

/**
 * Uses a movement link once: switches to its plan when the user could pick
 * it, else stays on the shown plan, then drops the link. Returns the plan
 * whose sheet opens, and a callback for when it has.
 */
export function useMovementLink(read: CoverageRead) {
  const {
    params,
    setParams
  } = useParams();
  const select = useCoveragePlanStore(state => state.select);
  const [linked, setLinked] = useState<string | null>(null);
  const shown = "plan" in read ? read.plan?.membershipId ?? null : null;
  const pickable = "otherPlans" in read ? read.otherPlans : [];
  const target = pickable.some(p => p.membershipId === params.membershipId) ? params.membershipId : shown;
  useEffect(() => {
    if (params.movement !== "1" || read.kind === "checking") {
      return;
    }
    if (target && target !== shown) {
      select(target);
      return;
    }
    // Only a plan view with a movement can open the sheet; any other link is dropped.
    setLinked(read.kind === "plan" && read.plan.movement ? shown : null);
    setParams({
      movement: undefined,
      membershipId: undefined
    }, {
      webBehavior: "replace"
    });
  }, [params.movement, read, target, shown, select, setParams]);
  return {
    linked,
    opened: useCallback(() => setLinked(null), [])
  };
}
export function MovementBanner({
  plan,
  linked,
  onLinkOpened
}: {
  plan: CoveragePlan;
  /** A movement link named this plan: open its sheet. */
  linked: boolean;
  onLinkOpened: () => void;
}) {
  const {
    notice,
    markSeen
  } = useMovementNotice(plan);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (linked && plan.movement) {
      setOpen(true);
      onLinkOpened();
    }
  }, [linked, plan.movement, onLinkOpened]);
  useEffect(() => {
    if (open) {
      markSeen();
    }
  }, [open, markSeen]);
  const shownAt = notice?.at.getTime();
  const kind = notice?.kind;
  useEffect(() => {
    if (shownAt !== undefined && kind !== undefined) {
      analyticsService.trackEvent("CoverageMovementNoticeShown", {
        kind
      });
    }
  }, [shownAt, kind]);
  return <>
      {notice ? <Notice notice={notice} onPress={() => {
      analyticsService.trackEvent("CoverageMovementNoticeOpened", {
        kind: notice.kind
      });
      setOpen(true);
    }} /> : null}
      {plan.movement ? <MovementSheet plan={plan} movement={plan.movement} open={open} onOpenChange={setOpen} /> : null}
    </>;
}
function Notice({
  notice,
  onPress
}: {
  notice: Movement;
  onPress: () => void;
}) {
  const amount = `${money(notice.delta)} · noticed ${shortDate(notice.at)}`;
  const title = `Your ${movementFigure(notice)} recently moved`;
  return <PressableStack aria-label={`↑ ${amount} · ${title}`} onPress={onPress} alignItems="center" gap="$sp1.5" paddingHorizontal="$sp2" paddingTop="$sp2" paddingBottom="$sp5" marginBottom="$-sp3" borderTopLeftRadius="$br3" borderTopRightRadius="$br3" backgroundColor="$bgWarning" data-sentry-element="PressableStack" data-sentry-component="Notice" data-sentry-source-file="MovementBanner.tsx">
      <YStack flex={1} gap="$sp0.5" data-sentry-element="YStack" data-sentry-source-file="MovementBanner.tsx">
        <XStack alignItems="center" gap="$sp0.25" data-sentry-element="XStack" data-sentry-source-file="MovementBanner.tsx">
          <ArrowUpIcon size="$iconXSmall" color="$iconWarning" data-sentry-element="ArrowUpIcon" data-sentry-source-file="MovementBanner.tsx" />
          <Body3 color="$textWarning" data-sentry-element="Body3" data-sentry-source-file="MovementBanner.tsx">{amount}</Body3>
        </XStack>
        <Subtitle2 color="$textDefault" data-sentry-element="Subtitle2" data-sentry-source-file="MovementBanner.tsx">{title}</Subtitle2>
      </YStack>
      <ChevronRightIcon size="$iconXSmall" color="$iconWarning" data-sentry-element="ChevronRightIcon" data-sentry-source-file="MovementBanner.tsx" />
    </PressableStack>;
}